import type { AccessSourceType, PermissionUpdateEntry } from '@thinkalpha/common/contracts/permissions.js';
import type { UpdateProviderRequest } from '@thinkalpha/locates-ws-client';
import type {
    Provider,
    ProviderAccessRequest,
    ProvidersResponse,
    Route,
    UpdateProviderResponse,
} from '@thinkalpha/ui-types/types/admin/lab';
import { lapi as axios } from 'src/api';

export const createProvider = (newProvider: ProviderAccessRequest): Promise<Provider> => {
    return axios.post<Provider>(`v1/providers`, newProvider).then((x) => x.data);
};
export const getFirmProviders = (firmId: string | null): Promise<Provider[]> | [] => {
    if (!firmId) return [];
    return axios.get<ProvidersResponse>(`/v1/providers/firms/${firmId}`).then((x) => x.data.providers);
};
export const getAccountProviders = (accountId: string | null): Promise<Provider[]> | [] => {
    if (!accountId) return [];
    return axios.get<ProvidersResponse>(`/v1/providers/accounts/${accountId}`).then((x) => x.data.providers);
};
export const getAllProviders = (firmId?: string): Promise<Provider[]> => {
    // `firmId` narrows the accessible list to providers that firm owns (PLAT-12660).
    return axios
        .get<ProvidersResponse>(`/v1/providers`, firmId ? { params: { firmId } } : undefined)
        .then((x) => x.data.providers);
};
/**
 * Provider PATCH input. `autoCancelAfterSeconds` follows the backend contract: omit to leave the
 * window untouched, a positive whole number sets it, `null` clears the provider override.
 */
export type ProviderConfigurationUpdate = Omit<Partial<Provider>, 'autoCancelAfterSeconds'> &
    Pick<UpdateProviderRequest, 'autoCancelAfterSeconds'>;

export const updateProviderConfiguration = (provider: ProviderConfigurationUpdate): Promise<Provider> => {
    return axios
        .patch<UpdateProviderResponse>(`/v1/providers/${provider.id}`, {
            defaultFictitiousName: provider.defaultFictitiousName,
            name: provider.name,
            enabled: provider.enabled,
            isPreBorrow: provider.features?.isPreBorrow ?? provider.isPreBorrow,
            supportsPartialAccept: provider.features?.supportsPartialAccept,
            supportsOddLots: provider.features?.supportsOddLots,
            supportsMixedLots: provider.features?.supportsMixedLots,
            // Billing directive (PLAT-12624). Shape assumed to mirror the GET response;
            // confirm against the PLAT-12551 update contract once it lands.
            billingDirective: provider.billingDirective && {
                enabled: provider.billingDirective.enabled,
                multiplier: provider.billingDirective.multiplier,
                flatMarkup: provider.billingDirective.flatMarkup,
                minPrice: provider.billingDirective.minPrice,
            },
            // Bid configuration (PLAT-12623). Shape assumed to mirror the GET response;
            // confirm against the PLAT-12552 update contract once it merges.
            bidConfig: provider.bidConfig,
            // Borrow availability windows (PLAT-12732). Shape mirrors the GET response; the backend
            // is aligning PATCH to accept everything GET returns (except id).
            borrowAvailability: provider.borrowAvailability,
            reportingKey: provider.reportingKey,
            autoCancelAfterSeconds: provider.autoCancelAfterSeconds,
        })
        .then((x) => x.data.provider);
};
/** Replace every entry `entityType`/`entityId` holds on the provider; see `setRoutePermission`. */
export const setProviderPermission = ({
    entityId,
    entityType,
    providerId,
    entries,
}: {
    entityId: string;
    entityType: AccessSourceType;
    providerId: string;
    entries: PermissionUpdateEntry[];
}): Promise<Route> => {
    return axios
        .put<Route>(`/v1/providers/${providerId}/permissions/${entityType}/${entityId}`, entries)
        .then((x) => x.data);
};
